<template>
  <el-card
    id="detailsSettings"
    v-loading="loading"
    shadow="hover"
    :body-style="{padding: '20px 30px'}"
    class="mainCard clear-card"
  >
    <div class="title">
      <div class="left defaultCursor">
        {{ $t('settings.details') }}
      </div>
      <div class="right">
        <el-button
          :type="edit ? 'plain' : 'primary'"
          round
          @click="clickEdit"
        >
          <span v-if="!edit">
            {{ $t('common.edit') }} <i class="el-icon-edit"></i>
          </span>
          <span v-else>
            {{ $t('common.save') }}
          </span>
        </el-button>
      </div>
    </div>

    <div class="content">
      <el-form
        ref="settingsForm"
        :model="settingsForm"
      >
        <el-row :gutter="20">
          <el-col :span="tripleCol">
            <el-form-item
              :label="$t('common.country')"
              class="smallSelectLabel"
            >
              <div v-if="!edit">
                <el-input
                  v-if="settingsForm.profile.country"
                  :value="settingsForm.profile.country"
                  disabled
                />
                <i
                  v-else
                  class="emptyPlaceholder"
                >
                  -
                </i>
              </div>
              <el-select
                v-else
                v-model="settingsForm.profile.country"
                :placeholder="$t('common.select')"
                filterable
                value-key="name"
              >
                <el-option
                  v-for="(country) in countries"
                  :key="country.id"
                  :label="country.name"
                  :value="country.name"
                />
              </el-select>
            </el-form-item>
          </el-col>
          <el-col :span="tripleCol">
            <el-form-item
              :label="$t('languages.language')"
              class="smallSelectLabel"
            >
              <div v-if="!edit">
                <el-input
                  v-if="settingsForm.profile.language"
                  :value="settingsForm.profile.language"
                  disabled
                />
                <i
                  v-else
                  class="emptyPlaceholder"
                >
                  -
                </i>
              </div>
              <el-select
                v-else
                v-model="settingsForm.profile.language"
                :placeholder="$t('common.select')"
                filterable
                value-key="id"
              >
                <el-option
                  v-for="(language, lKey) in languages"
                  :key="lKey"
                  :label="language.value"
                  :value="language.value"
                />
              </el-select>
            </el-form-item>
          </el-col>
        </el-row>

        <el-row
          :gutter="50"
          class="others"
        >
          <el-col :span="dualColQuick">
            <div class="APReminder">
              <div class="title actionPlan">
                {{ $t('settings.apSurveyReminder') }}
                <i
                  v-if="edit"
                  class="apTime"
                >
                  {{ $t('settings.sentAt9am') }}
                </i>
              </div>

              <div class="APReminderSelect">
                <el-row :gutter="15">
                  <el-col :span="24">
                    <el-form-item>
                      <div v-if="!edit">
                        <span v-if="displayPeriods">
                          {{ displayPeriods }}
                        </span>
                        <i
                          v-else
                          class="emptyPlaceholder"
                        >
                          -
                        </i>
                      </div>

                      <el-select
                        v-else
                        v-model="settingsForm.profile.ap_reminder"
                        :placeholder="$t('common.select')"
                      >
                        <el-option
                          v-for="(period, key) in periods"
                          :key="key"
                          :label="period.label"
                          :value="period.value"
                        />
                      </el-select>
                    </el-form-item>
                  </el-col>

                  <el-col :span="24">
                    <el-form-item>
                      <el-checkbox-group
                        v-if="settingsForm.profile.ap_reminder === 'daily'"
                        v-model="selectedDays"
                        size="small"
                        :min="1"
                        :disabled="!edit"
                      >
                        <el-checkbox-button
                          v-for="(day, index) in days"
                          :key="index"
                          :label="day.value"
                        >
                          {{ day.label }}
                        </el-checkbox-button>
                      </el-checkbox-group>
                    </el-form-item>
                  </el-col>
                </el-row>
              </div>
            </div>
          </el-col>
        </el-row>
      </el-form>
    </div>
  </el-card>
</template>

<script>
import { mapState, mapActions, mapMutations } from 'vuex';
import api from '@/api';

export default {
  name: 'DetailsSettings',

  data() {
    return {
      periods: [
        {
          label: this.$t('settings.daily'),
          value: 'daily',
        },
        {
          label: this.$t('settings.never'),
          value: 'none',
        },
      ],
      days: [
        {
          label: this.$t('settings.days.short.mon'),
          value: 'Mon',
        },
        {
          label: this.$t('settings.days.short.tue'),
          value: 'Tue',
        },
        {
          label: this.$t('settings.days.short.wed'),
          value: 'Wed',
        },
        {
          label: this.$t('settings.days.short.thu'),
          value: 'Thu',
        },
        {
          label: this.$t('settings.days.short.fri'),
          value: 'Fri',
        },
        {
          label: this.$t('settings.days.short.sat'),
          value: 'Sat',
        },
        {
          label: this.$t('settings.days.short.sun'),
          value: 'Sun',
        },
      ],
      edit: false,
      loading: false,
      settingsForm: {
        profile: {
          country: null,
          language: null,
          ap_reminder: null,
          ap_reminder_settings: {
            days_list: [],
          },
        },
      },

      timezones: [],
      countries: [],
      languages: [],
      selectedCountry: null,
      selectedDays: [],
    };
  },

  computed: {
    ...mapState({
      user: (state) => state.user,
    }),

    displayPeriods() {
      const value = this.settingsForm.profile.ap_reminder;
      if (!value) return '';

      const period = this.periods.find((per) => per.value === value);

      if (period) {
        return period.label;
      }
      return '';
    },

    tripleCol() {
      let col = 8;
      if (this.$mq === 'mobile') {
        col = 24;
      } else if (this.$mq === 'tablet') {
        col = 12;
      }
      return col;
    },

    dualColQuick() {
      let col = 12;
      if (['mobile', 'tablet'].includes(this.$mq)) {
        col = 24;
      }
      return col;
    },
  },

  async created() {
    this.getCountries();
    this.getLanguages();
    this.initForm(this.user);
  },

  methods: {
    ...mapActions('processStatus', [
      'fetchProcess',
    ]),

    ...mapMutations('user', [
      'loginUser',
    ]),

    async getCountries() {
      try {
        this.loading = true;
        const response = await api.getCountriesList();
        this.countries = response.data;
      } catch (err) {
        console.log(err);
        if (err.msg) {
          this.$message.error(err.msg);
        }
      } finally {
        this.loading = false;
      }
    },

    async getLanguages() {
      try {
        this.loading = false;
        const response = await api.getCoachLanguages();
        this.languages = response.data;
      } catch (err) {
        console.log(err);
        if (err.msg) {
          this.$message.error(err.msg);
        }
      } finally {
        this.loading = false;
      }
    },

    initForm(data) {
      this.settingsForm.profile.country = data.profile.country;
      this.settingsForm.profile.language = data.profile.language;
      this.settingsForm.profile.ap_reminder = data.profile.ap_reminder;
      this.settingsForm.profile.ap_reminder_settings = data.profile.ap_reminder_settings;

      if (!this.settingsForm.profile.ap_reminder_settings.days_list) {
        this.settingsForm.profile.ap_reminder_settings.days_list = [];
      }
      this.selectedDays = this.settingsForm.profile.ap_reminder_settings.days_list;
    },

    async clickEdit() {
      if (this.edit) {
        await this.saveSettings();
      } else {
        this.edit = !this.edit;
      }
    },

    async saveSettings() {
      try {
        this.loading = true;
        this.settingsForm.profile.ap_reminder_settings.days_list = this.selectedDays;
        const response = await api.updateUserProfile(this.settingsForm);
        this.initForm(response.data);
        this.$message.success('Your settings have been saved');
        await this.loginUser(response.data);
        this.fetchProcess();
        this.edit = false;
      } catch (err) {
        console.log(err);
        if (err.msg) {
          this.$message.error(err.msg);
        }
      } finally {
        this.loading = false;
      }
    },
  },
};
</script>

<style lang="scss" scoped>
#detailsSettings {
  .emptyPlaceholder {
    position: relative;
    width: 100%;
    display: inline-block;
    font-size: 16px;
  }

  .el-row.others {
    .el-col > div:last-child {
      margin-bottom: 10px;
    }
  }

  .title {
    margin-bottom: 12px;
    font-weight: bold;
    display: flex;
    justify-content: space-between;
    align-items: center;
    cursor: default;

    .left {
      font-size: 16px;
    }

    &.actionPlan {
      justify-content: normal;

      .apTime {
        padding: 0 4px;
        font-size: 14px;
        font-weight: normal;
      }
    }
  }

  .el-row {
    margin-bottom: 20px;

    .APReminder {
      margin-bottom: 50px;
    }

    &:last-child {
      margin: 0;
    }

    .timeZoneItem {
      margin: 0 0 10px;
    }
  }

  .password {
    padding-bottom: 5px;

    .changePasswordBtn {
      background-color: transparent;
      color: white;
    }
  }

  .warning {
    color: #f56c6c;
    font-size: 14px;
  }
}
</style>
